import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuthContext } from "./AuthContext";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Wraps authenticated routes.
 * - If loading: shows a full-screen spinner (avoids flash of login page)
 * - If unauthenticated: redirects to /auth/login, preserving the intended path
 * - If authenticated: renders children
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoading, isAuthenticated } = useAuthContext();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #fcf8fa 0%, #ffffff 100%)",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "3px solid rgba(255,59,142,0.2)",
            borderTopColor: "#ff3b8e",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/auth/login" state={{ from: location.pathname }} replace />
    );
  }

  return <>{children}</>;
}

/**
 * Redirects already-authenticated users away from auth pages.
 * Useful on /auth/login — if you already have a session, go to /app.
 */
export function PublicOnlyRoute({ children }: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, user } = useAuthContext();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #fcf8fa 0%, #ffffff 100%)",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "3px solid rgba(255,59,142,0.2)",
            borderTopColor: "#ff3b8e",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Anonymous sessions are intentionally allowed to reach login so a web
  // visitor can promote the checkout session to an email-backed identity.
  if (isAuthenticated && (user?.email || user?.appleUserId)) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
